import { type PortfolioBreakdownHierarchyNode } from 'venn-components';

import { type ColorStrategy } from './fillColorMap';
import { findRcmStrategyColor } from './findRcmStrategyColor';

/**
 * Fills color mapping that uses RCM strategy colors for known strategies
 * and fallback colors (e.g., investments colors with opacity) for non-strategy nodes
 *
 * @param colorMap - Accumulator for ID-to-color mappings
 * @param nodes - Portfolio hierarchy nodes to process
 * @param parentPath - Accumulated path of labels from root to current level
 * @param parentStrategyColor - Color from nearest ancestor with a strategy match
 * @param fallbackColorStrategy - Optional function to generate colors for non-strategy nodes
 */
export function fillRcmColorMap(
  colorMap: Map<string, string>,
  nodes: PortfolioBreakdownHierarchyNode[],
  parentPath: string[] = [],
  parentStrategyColor?: string,
  fallbackColorStrategy?: ColorStrategy,
) {
  if (!nodes?.length) {
    return;
  }

  nodes.forEach((node, index) => {
    const currentPath = [...parentPath, node.label];

    // Try to find a direct strategy color match for this path
    const strategyColor = findRcmStrategyColor(currentPath);

    const nodeColor =
      strategyColor ??
      (parentStrategyColor &&
        fallbackColorStrategy?.({
          parentColor: parentStrategyColor,
          childrenCount: nodes.length,
          childIndex: index,
        }));

    if (nodeColor) {
      colorMap.set(node.id, nodeColor);
    }

    if (node.children?.length) {
      // Inherit only strategy colors, not fallback colors
      // Calculated ones will be defined after hierarchy filtering by selected strategy levels.
      fillRcmColorMap(colorMap, node.children, currentPath, strategyColor, fallbackColorStrategy);
    }
  });
}
